import { lazy, useCallback, useMemo, startTransition, Suspense, useEffect, useRef, useState } from 'react';
import {
  AnimatePresence,
  motion as Motion,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useTransform,
  useVelocity,
} from 'framer-motion';
import {
  BrainCircuit,
  Code2,
  Database,
  Layers3,
  Sparkles,
} from 'lucide-react';
import { SDMonogram } from './components/branding/BrandMark';
import CaseStudyPreview from './components/portfolio/CaseStudyPreview';
import FeaturedProjectShowcase from './components/portfolio/FeaturedProjectShowcase';
import CompanyHistoryTimeline from './components/sections/CompanyHistoryTimeline';
import FoundersProfileCards from './components/sections/FoundersProfileCards';
import HeroSection from './components/sections/HeroSection';
import VisionSection from './components/sections/VisionSection';
import CapabilitiesSection from './components/sections/CapabilitiesSection';
import MagneticButton from './components/ui/MagneticButton';
import NeuralNavbar from './components/ui/NeuralNavbar';
import { portfolioContent } from './content/portfolioContent';
import {
  fetchPortfolioContent,
  fetchSystemSnapshot,
} from './lib/api';
import ContactSection from './components/sections/ContactSection';
import SiteBackground from './SiteBackground';
import Cursor from './components/ui/Cursor';

const EASE = [0.22, 1, 0.36, 1];
const Scene = lazy(() => import('./components/canvas/Scene'));

const SCROLL_TO_SCENE = [
  [0, 0.12],
  [0.12, 0.25],
  [0.25, 0.4],
  [0.4, 0.52],
  [0.52, 0.68],
  [0.68, 0.85],
  [0.85, 1],
];
const SCENE_IDS = ['hero', 'origin', 'skills', 'process', 'projects', 'philosophy', 'contact'];

function scrollProgressToScene(progress) {
  for (let i = 0; i < SCROLL_TO_SCENE.length; i++) {
    const [min, max] = SCROLL_TO_SCENE[i];
    if (progress >= min && progress < max) return SCENE_IDS[i];
  }
  return 'contact';
}

const DEFAULT_SYSTEM_SNAPSHOT = {
  cpuPercent: 0,
  memoryPercent: 0,
  localTime: '--:--',
  activeCores: 0,
  lightingMode: 'sapphire-night',
  loadIntensity: 0.25,
  isPeakWorkHours: false,
};

const LIGHTING_MODE_LABELS = {
  'sapphire-night': 'Sapphire studio mode',
  'dawn-cyan': 'Morning build cycle',
  'neon-storm': 'Peak execution window',
};

const SKILL_ICON_MAP = {
  layout: Layers3,
  database: Database,
  brain: BrainCircuit,
  code: Code2,
};

const AMBIENT_PARTICLES = Array.from({ length: 18 }, (_, index) => ({
  id: index,
  left: 4 + ((index * 11) % 92),
  top: 8 + ((index * 17) % 76),
  size: 2.5 + (index % 4) * 1.4,
  duration: 16 + (index % 5) * 2.8,
  delay: index * 0.36,
  driftX: (index % 2 === 0 ? 16 : -14) + (index % 3) * 4,
  driftY: -14 - (index % 4) * 5,
  opacity: 0.12 + (index % 4) * 0.045,
  scale: 0.82 + (index % 3) * 0.16,
}));

const AMBIENT_RAYS = [
  { id: 'ray-1', left: '6%', top: '-18%', width: '24rem', height: '96vh', rotate: -18, duration: 24, delay: 0.2, driftX: 6, driftY: 10, opacity: 0.14 },
  { id: 'ray-2', left: '38%', top: '-22%', width: '20rem', height: '88vh', rotate: -8, duration: 20, delay: 0.8, driftX: -5, driftY: 8, opacity: 0.1 },
  { id: 'ray-3', left: '72%', top: '-16%', width: '22rem', height: '92vh', rotate: 12, duration: 26, delay: 0.5, driftX: 4, driftY: 12, opacity: 0.12 },
];

const AMBIENT_CURRENTS = [
  { id: 'current-1', left: '-12%', top: '24%', width: '34rem', rotate: 10, duration: 18, delay: 0.2, driftX: 42, driftY: -10, opacity: 0.16 },
  { id: 'current-2', left: '12%', top: '56%', width: '28rem', rotate: -8, duration: 20, delay: 0.7, driftX: -36, driftY: 8, opacity: 0.14 },
  { id: 'current-3', left: '54%', top: '32%', width: '32rem', rotate: 14, duration: 22, delay: 0.4, driftX: 34, driftY: -12, opacity: 0.15 },
  { id: 'current-4', left: '70%', top: '68%', width: '26rem', rotate: -12, duration: 19, delay: 1, driftX: -30, driftY: 10, opacity: 0.12 },
];

const LOADER_PARTICLES = Array.from({ length: 12 }, (_, index) => ({
  id: index,
  left: 10 + ((index * 11) % 80),
  top: 18 + ((index * 14) % 52),
  size: 4 + (index % 3) * 2,
  duration: 2 + (index % 4) * 0.18,
  delay: index * 0.08,
}));

function Reveal({ children, className = '', delay = 0, reducedMotion = false }) {
  if (reducedMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <Motion.div
      className={className}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.82,
          delay,
          ease: EASE,
        },
      }}
      viewport={{ once: true, amount: 0.15 }}
    >
      {children}
    </Motion.div>
  );
}

function SectionHeader({ eyebrow, title, description, reducedMotion = false, sectionNumber, headingId }) {
  const headingRef = useRef(null);
  const ghostRef = useRef(null);

  useEffect(() => {
    if (reducedMotion) return;
    const el = headingRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-visible');
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [reducedMotion]);

  useEffect(() => {
    if (reducedMotion || !sectionNumber) return;
    const el = ghostRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-visible');
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [reducedMotion, sectionNumber]);

  if (reducedMotion) {
    return (
      <div className="section-header">
        <span className="eyebrow">{eyebrow}</span>
        <h2 id={headingId} className="section-title">{title}</h2>
        {description ? <p className="section-description">{description}</p> : null}
      </div>
    );
  }

  return (
    <div className="section-header" style={{ position: 'relative' }}>
      {sectionNumber && (
        <span ref={ghostRef} className="section-ghost-number" aria-hidden="true">
          {String(sectionNumber).padStart(2, '0')}
        </span>
      )}
      <Reveal reducedMotion={reducedMotion}>
        <span className="eyebrow blur-reveal">{eyebrow}</span>
      </Reveal>
      <div ref={headingRef} className="reveal-heading">
        <h2 id={headingId} className="section-title blur-reveal">
          <span className="reveal-heading__line">{title}</span>
        </h2>
        <span className="reveal-heading__bar" />
      </div>
      {description ? (
        <Reveal reducedMotion={reducedMotion} delay={0.12}>
          <p className="section-description blur-reveal">{description}</p>
        </Reveal>
      ) : null}
    </div>
  );
}

/* Signal Awakening: near black → plankton → two currents L/R → converge → core pulse → SD logo fade */
function LoaderScreen({ content, onSkip, reducedMotion }) {
  const EASE_SMOOTH = [0.22, 1, 0.36, 1];

  return (
    <Motion.div
      className="loader-screen"
      initial={{ opacity: 1 }}
      exit={{
        opacity: 0,
        transition: { duration: reducedMotion ? 0.25 : 0.82, ease: EASE_SMOOTH },
      }}
    >
      <div className="loader-screen__glow" />

      {!reducedMotion && (
        <>
          <Motion.div
            className="loader-screen__current loader-screen__current--left"
            initial={{ opacity: 0, x: '-60%' }}
            animate={{ opacity: 0.5, x: '-8%' }}
            transition={{ duration: 1.4, delay: 0.5, ease: EASE_SMOOTH }}
          />
          <Motion.div
            className="loader-screen__current loader-screen__current--right"
            initial={{ opacity: 0, x: '60%' }}
            animate={{ opacity: 0.5, x: '8%' }}
            transition={{ duration: 1.4, delay: 0.5, ease: EASE_SMOOTH }}
          />
        </>
      )}

      {LOADER_PARTICLES.map((particle) => (
        <Motion.span
          key={particle.id}
          className="loader-screen__particle"
          style={{
            left: `${particle.left}%`,
            top: `${particle.top}%`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
          }}
          initial={reducedMotion ? undefined : { opacity: 0 }}
          animate={
            reducedMotion
              ? undefined
              : {
                  opacity: [0, 0.6, 0.5],
                  scale: [0.5, 1, 0.9],
                  y: [0, -18, -6],
                }
          }
          transition={
            reducedMotion
              ? undefined
              : {
                  duration: particle.duration,
                  delay: particle.delay + 0.3,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: [0.22, 1, 0.36, 1],
                }
          }
        />
      ))}

      <div className="loader-mark">
        <Motion.div
          className="loader-mark__core"
          initial={reducedMotion ? undefined : { scale: 0.92, opacity: 0.7 }}
          animate={
            reducedMotion
              ? undefined
              : { scale: [0.95, 1.06, 1.02], opacity: 1 }
          }
          transition={
            reducedMotion
              ? undefined
              : { duration: 2, delay: 1.2, ease: EASE_SMOOTH }
          }
        >
          <div className="loader-mark__ring loader-mark__ring--outer" />
          <div className="loader-mark__ring loader-mark__ring--inner" />
          <Motion.div
            className="loader-mark__sweep"
            initial={reducedMotion ? undefined : { x: '-160%' }}
            animate={reducedMotion ? undefined : { x: '180%' }}
            transition={
              reducedMotion
                ? undefined
                : { duration: 1.4, delay: 1.4, ease: EASE_SMOOTH }
            }
          />
          <SDMonogram className="loader-mark__monogram" compact />
        </Motion.div>

        <Motion.div
          className="loader-copy"
          initial={reducedMotion ? undefined : { opacity: 0, y: 12 }}
          animate={reducedMotion ? undefined : { opacity: 1, y: 0 }}
          transition={
            reducedMotion
              ? undefined
              : { duration: 0.9, delay: 2.1, ease: EASE_SMOOTH }
          }
        >
          <p className="loader-copy__eyebrow">
            {content.loader?.eyebrow || content.brand}
          </p>
          <h1 className="loader-copy__title" aria-hidden="true">{content.loader?.title}</h1>
          <p className="loader-copy__status">{content.loader?.status}</p>
        </Motion.div>
      </div>

      <button
        type="button"
        className="loader-screen__skip"
        onClick={onSkip}
        aria-label="Skip intro animation"
      >
        Skip Intro
      </button>
    </Motion.div>
  );
}

function AmbientBackdrop({ scrollYProgress, reducedMotion, isMobileViewport = false }) {
  const depthX = useTransform(scrollYProgress, [0, 1], ['0%', '4%']);
  const depthY = useTransform(scrollYProgress, [0, 1], ['0%', '-7%']);
  const atmosphereY = useTransform(scrollYProgress, [0, 1], ['0%', '8%']);
  const currentY = useTransform(scrollYProgress, [0, 1], ['0%', '-10%']);
  const particleY = useTransform(scrollYProgress, [0, 1], ['0%', '6%']);
  const ambientRays = isMobileViewport ? AMBIENT_RAYS.slice(0, 2) : AMBIENT_RAYS;
  const ambientCurrents = isMobileViewport ? AMBIENT_CURRENTS.slice(0, 2) : AMBIENT_CURRENTS;
  const ambientParticles = isMobileViewport
    ? AMBIENT_PARTICLES.filter((_, index) => index % 2 === 0)
    : AMBIENT_PARTICLES;

  return (
    <div className="ambient-backdrop" aria-hidden="true">
      <Motion.div
        className="ambient-backdrop__layer ambient-backdrop__layer--depth"
        style={{ x: depthX, y: depthY }}
      />
      <Motion.div
        className="ambient-backdrop__layer ambient-backdrop__layer--atmosphere"
        style={{ y: atmosphereY }}
      />
      <div className="ambient-backdrop__layer ambient-backdrop__layer--vignette" />

      <div className="ambient-backdrop__ray-field">
        {ambientRays.map((ray) => (
          <span
            key={ray.id}
            className="ambient-backdrop__ray"
            style={{
              left: ray.left,
              top: ray.top,
              width: ray.width,
              height: ray.height,
              transform: `rotate(${ray.rotate}deg)`,
              '--ray-opacity': ray.opacity,
              '--ray-driftX': ray.driftX,
              '--ray-driftY': ray.driftY,
              '--ray-duration': `${ray.duration}s`,
              '--ray-delay': `${ray.delay}s`,
            }}
          />
        ))}
      </div>

      <Motion.div className="ambient-backdrop__current-field" style={{ y: currentY }}>
        {ambientCurrents.map((current) => (
          <span
            key={current.id}
            className="ambient-backdrop__current"
            style={{
              left: current.left,
              top: current.top,
              width: current.width,
              transform: `rotate(${current.rotate}deg)`,
              '--cur-opacity': current.opacity,
              '--cur-driftX': current.driftX,
              '--cur-driftY': current.driftY,
              '--cur-duration': `${current.duration}s`,
              '--cur-delay': `${current.delay}s`,
            }}
          />
        ))}
      </Motion.div>

      <Motion.div className="ambient-backdrop__particle-field" style={{ y: particleY }}>
        {ambientParticles.map((particle) => (
          <span
            key={particle.id}
            className="ambient-backdrop__particle"
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              '--p-opacity': particle.opacity,
              '--p-scale': particle.scale,
              '--p-driftX': particle.driftX,
              '--p-driftY': particle.driftY,
              '--p-duration': `${particle.duration}s`,
              '--p-delay': `${particle.delay}s`,
            }}
          />
        ))}
      </Motion.div>
    </div>
  );
}

// HeroSection is now the standalone component from ./components/sections/HeroSection

function FounderSection({ content, reducedMotion }) {
  return (
    <div id="founders">
      <section id="founders-overview" aria-labelledby="founders-heading" className="story-section story-section--founders-overview">
        <h2 id="founders-heading" className="sr-only">Our Founders</h2>
        <div className="section-shell">
          <FoundersProfileCards
            founders={content.founders}
            reducedMotion={reducedMotion}
          />
        </div>
      </section>
      <CompanyHistoryTimeline content={content.founders} reducedMotion={reducedMotion} />
    </div>
  );
}



function ProcessSection({ content, reducedMotion }) {
  const lineRef = useRef(null);

  useEffect(() => {
    if (reducedMotion) return;
    const el = lineRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-drawing');
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [reducedMotion]);

  return (
    <section id="process" aria-labelledby="process-heading" className="story-section story-section--process">
      <div className="section-shell">
        <SectionHeader
          eyebrow={content.process.eyebrow}
          title={content.process.title}
          description={content.process.description}
          reducedMotion={reducedMotion}
          sectionNumber={4}
          headingId="process-heading"
        />

        <div className="glass-card process-track" style={{ position: 'relative' }}>
          {!reducedMotion && (
            <svg className="process-svg-line" aria-hidden="true">
              <line x1="1" y1="0" x2="1" y2="100%" className="process-svg-line__track" />
              <line ref={lineRef} x1="1" y1="0" x2="1" y2="100%" className="process-svg-line__draw" />
            </svg>
          )}

          <div className="process-grid">
            {content.process.steps.map((step, index) => (
              <Reveal key={step.name} reducedMotion={reducedMotion} delay={index * 0.08}>
                <article className="process-step">
                  <div className="process-step__dot" />
                  <span className="process-step__index">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3>{step.name}</h3>
                  <p>{step.summary}</p>
                  <strong>{step.outcome}</strong>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function WipeReveal({ children, delay = 0, reducedMotion = false }) {
  const ref = useRef(null);

  useEffect(() => {
    if (reducedMotion) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => el.classList.add('is-revealed'), delay * 1000);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [reducedMotion, delay]);

  if (reducedMotion) return children;

  return (
    <div ref={ref} className="wipe-wrap">
      <div className="wipe-wrap__overlay" />
      <div className="wipe-wrap__content">{children}</div>
    </div>
  );
}

const CARD_WIDTH = 280 + 24; // card width + gap
const VISIBLE_COUNT = 2;

function PosterCard({ project, isFlipped, onFlip }) {
  return (
    <div
      className={`project-poster ${isFlipped ? 'is-flipped' : ''}`}
      onClick={onFlip}
      role="button"
      tabIndex={0}
      aria-label={`${project.title} — click to flip`}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onFlip(); }}
    >
      <div className="project-poster__inner">
        {/* Front */}
        <div className="project-poster__face project-poster__front">
          <div className="project-poster__preview">
            {project.image ? (
              <img src={project.image} alt={`${project.title} preview`} />
            ) : (
              <CaseStudyPreview variant={project.preview} label={project.mediaLabel} />
            )}
          </div>
          <div className="project-poster__front-body">
            <p className="project-poster__badge">{project.badge}</p>
            <h3 className="project-poster__title">{project.title}</h3>
            <div className="project-poster__tags">
              {project.tech?.slice(0, 3).map((t) => (
                <span key={t} className="project-poster__tag">{t}</span>
              ))}
            </div>
          </div>
        </div>
        {/* Back */}
        <div className="project-poster__face project-poster__back">
          <h3 className="project-poster__back-title">{project.title}</h3>
          {[
            { label: 'Problem', text: project.problem },
            { label: 'Solution', text: project.solution },
            { label: 'Impact', text: project.impact },
          ].map(({ label, text }) => text && (
            <div key={label} className="project-poster__story-item">
              <span className="project-poster__story-label">{label}</span>
              <p className="project-poster__story-text">{text}</p>
            </div>
          ))}
          {project.link && (
            <a
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              className="project-poster__live-link"
              onClick={(e) => e.stopPropagation()}
            >
              View Live ↗
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function ProjectsSection({ content, reducedMotion }) {
  const sectionRef = useRef(null);
  const [flippedIndex, setFlippedIndex] = useState(null);
  const [activeDot, setActiveDot] = useState(0);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 767
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = () => setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const projects = content.projects || [];
  const cardCount = projects.length;
  const totalScrollExtra = cardCount * 300;

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end end'],
  });

  const translateX = useTransform(
    scrollYProgress,
    [0, 1],
    ['0px', `-${Math.max(0, cardCount - VISIBLE_COUNT) * CARD_WIDTH}px`]
  );

  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    const idx = Math.round(v * Math.max(0, cardCount - 1));
    setActiveDot(Math.min(idx, cardCount - 1));
  });

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape') setFlippedIndex(null);
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  function handleFlip(index) {
    setFlippedIndex((prev) => (prev === index ? null : index));
  }

  if (reducedMotion) {
    return (
      <section id="projects" aria-labelledby="projects-heading" className="story-section story-section--projects">
        <div className="section-shell">
          <SectionHeader
            eyebrow="Projects / Case Studies"
            title="The most grounded section of the experience: proof of thinking, craft, and delivery."
            reducedMotion={reducedMotion}
            sectionNumber={5}
            headingId="projects-heading"
          />
          <div className="project-stack">
            {projects.map((project, index) => (
              <WipeReveal key={project.title} reducedMotion={reducedMotion} delay={index * 0.1}>
                {project.featured ? (
                  <FeaturedProjectShowcase project={project} reducedMotion={reducedMotion} />
                ) : (
                  <article className={`glass-card project-card ${index % 2 === 1 ? 'is-reversed' : ''}`}>
                    <div className="project-card__content">
                      <h3>{project.title}</h3>
                      <p className="project-card__summary">{project.summary}</p>
                    </div>
                  </article>
                )}
              </WipeReveal>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <div
      ref={sectionRef}
      id="projects"
      aria-labelledby="projects-heading"
      className="projects-pinned-wrapper"
      style={isMobile ? undefined : { height: `calc(100vh + ${totalScrollExtra}px)` }}
    >
      <div className="projects-pinned-inner">
        <div className="section-shell" style={{ width: '100%', flexShrink: 0, paddingBottom: '1rem' }}>
          <SectionHeader
            eyebrow="Projects / Case Studies"
            title="Real work. Real impact."
            reducedMotion={reducedMotion}
            sectionNumber={5}
            headingId="projects-heading"
          />
        </div>
        <Motion.div className="projects-track" style={isMobile ? undefined : { x: translateX }}>
          {projects.map((project, index) => (
            <PosterCard
              key={project.title}
              project={project}
              isFlipped={flippedIndex === index}
              onFlip={() => handleFlip(index)}
            />
          ))}
        </Motion.div>
        {!isMobile && (
          <div className="projects-dots" aria-hidden="true">
            {projects.map((_, i) => (
              <span key={i} className={`projects-dot ${activeDot === i ? 'is-active' : ''}`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PhilosophySection({ content, reducedMotion }) {
  return <VisionSection content={content} reducedMotion={reducedMotion} />;
}

function TrustBandSection({ content, reducedMotion }) {
  const band = content.trustBand || {
    eyebrow: 'How we work',
    title: 'Client confidence.',
    statements: [],
  };

  return (
    <section id="trust" aria-labelledby="trust-heading" className="story-section story-section--trust">
      <div className="section-shell">
        <SectionHeader
          eyebrow={band.eyebrow}
          title={band.title}
          reducedMotion={reducedMotion}
          sectionNumber={7}
          headingId="trust-heading"
        />
        <div className="trust-band">
          {band.statements.map((item, index) => (
            <Reveal key={item.label} reducedMotion={reducedMotion} delay={index * 0.05}>
              <div className="trust-band__item blur-reveal">
                <span className="trust-band__label">{item.label}</span>
                <p className="trust-band__text">{item.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}


function spawnFooterParticles(e) {
  const anchor = e.currentTarget;
  const rect = anchor.getBoundingClientRect();
  const count = 6;
  for (let i = 0; i < count; i++) {
    const span = document.createElement('span');
    span.className = 'footer-particle';
    const px = (Math.random() - 0.5) * 44;
    span.style.cssText = `
      left: ${rect.left + rect.width / 2 - 1.5}px;
      top: ${rect.top - 2}px;
      --px: ${px}px;
      animation-delay: ${i * 0.05}s;
    `;
    document.body.appendChild(span);
    span.addEventListener('animationend', () => span.remove(), { once: true });
  }
}

function Footer({ brand, contact }) {
  const currentYear = new Date().getFullYear();
  const footerContact = {
    email: 'sanjivanidevworks@gmail.com',
    emailHref: 'mailto:sanjivanidevworks@gmail.com',
    phone: '+91 88662 25485',
    phoneHref: 'tel:+918866225485',
    location: 'Ahmedabad, Gujarat, India',
    availabilityShort: 'Available for select projects in 2026',
    ...contact,
  };

  const socialLinks = [
    { label: 'GitHub', href: 'https://github.com/SahilJoshi-19' },
    { label: 'LinkedIn', href: 'https://www.linkedin.com/company/sanjivanidevworks' },
  ];

  return (
    <footer className="portfolio-footer" aria-label="Site footer">
      <div className="portfolio-footer__inner">
        <div className="portfolio-footer__mark">
          <SDMonogram className="portfolio-footer__monogram" compact rings={false} />
        </div>
        <div className="portfolio-footer__contact">
          <a href={footerContact.phoneHref}>{footerContact.phone}</a>
          <span aria-hidden="true">•</span>
          <a href={footerContact.emailHref}>{footerContact.email}</a>
        </div>
        <div className="portfolio-footer__social">
          {socialLinks.map(({ label, href }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="portfolio-footer__social-link"
              onMouseEnter={spawnFooterParticles}
            >
              {label}
            </a>
          ))}
        </div>
        <p>
          {brand} is a two-founder development company creating digital products that
          feel visionary, structured, and ready for serious clients.
        </p>
        <p className="portfolio-footer__meta">
          {footerContact.location} · {footerContact.availabilityShort} · © {currentYear}
        </p>
      </div>
    </footer>
  );
}

function PageCurtain() {
  return (
    <Motion.div
      initial={{ scaleX: 1 }}
      animate={{ scaleX: 0 }}
      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      style={{
        position: 'fixed',
        inset: 0,
        background: '#050b18',
        zIndex: 9999,
        transformOrigin: 'right',
        pointerEvents: 'none',
      }}
      aria-hidden="true"
    />
  );
}

function useBlurReveal() {
  useEffect(() => {
    const elements = document.querySelectorAll('.blur-reveal');
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

function App() {
  const reducedMotion = useReducedMotion();
  const [content, setContent] = useState(portfolioContent);
  const [systemSnapshot, setSystemSnapshot] = useState(DEFAULT_SYSTEM_SNAPSHOT);
  const [showLoader, setShowLoader] = useState(false);
  const [sceneReady, setSceneReady] = useState(false);
  const [activeScene, setActiveScene] = useState('hero');
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false,
  );

  const pointerXRef = useRef(0);
  const pointerYRef = useRef(0);
  const loaderTimerRef = useRef(null);

  const { scrollY, scrollYProgress } = useScroll();
  const scrollVelocity = useVelocity(scrollY);
  const navScale = useTransform(scrollY, [0, 240], [1, 0.94]);
  const navY = useTransform(scrollY, [0, 240], [0, -10]);

  const motionState = useMemo(
    () => ({
      pointerX: { get: () => pointerXRef.current },
      pointerY: { get: () => pointerYRef.current },
      storyProgress: { get: () => scrollYProgress.get() },
    }),
    [scrollYProgress]
  );

  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    setActiveScene(scrollProgressToScene(v));
  });

  useEffect(() => {
    const media = window.matchMedia('(max-width: 767px)');
    const syncViewport = () => {
      setIsMobileViewport(media.matches);
    };

    syncViewport();
    media.addEventListener('change', syncViewport);

    return () => {
      media.removeEventListener('change', syncViewport);
    };
  }, []);

  const dismissLoader = useCallback(() => {
    if (loaderTimerRef.current) {
      window.clearTimeout(loaderTimerRef.current);
      loaderTimerRef.current = null;
    }

    setShowLoader(false);
  }, []);

  useEffect(() => {
    function handlePointerMove(e) {
      pointerXRef.current = (e.clientX / window.innerWidth) * 2 - 1;
      pointerYRef.current = -(e.clientY / window.innerHeight) * 2 + 1;
    }

    window.addEventListener('pointermove', handlePointerMove);
    return () => window.removeEventListener('pointermove', handlePointerMove);
  }, []);

  useEffect(() => {
    const duration = reducedMotion ? 0 : 600;

    loaderTimerRef.current = window.setTimeout(() => {
      dismissLoader();
    }, duration);

    return () => {
      if (loaderTimerRef.current) {
        window.clearTimeout(loaderTimerRef.current);
        loaderTimerRef.current = null;
      }
    };
  }, [dismissLoader, reducedMotion]);

  useEffect(() => {
    if (!showLoader) return undefined;

    function handleLoaderKeydown(event) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Escape') {
        event.preventDefault();
        dismissLoader();
      }
    }

    window.addEventListener('keydown', handleLoaderKeydown);
    return () => {
      window.removeEventListener('keydown', handleLoaderKeydown);
    };
  }, [dismissLoader, showLoader]);

  useEffect(() => {
    const controller = new AbortController();

    async function syncContent() {
      try {
        const liveContent = await fetchPortfolioContent(controller.signal);
        startTransition(() => {
          setContent(liveContent);
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          setContent(portfolioContent);
        }
      }
    }

    syncContent();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    fetchSystemSnapshot().then((snapshot) => {
      if (isMounted) setSystemSnapshot(snapshot);
    }).catch(() => {});

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(() => setSceneReady(true), { timeout: 2000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(() => setSceneReady(true), 800);
    return () => window.clearTimeout(id);
  }, []);

  useBlurReveal();

  const sceneActive = showLoader ? 'loader' : activeScene;

  /* Fade 3D scene out as user scrolls past hero — lets SiteBackground show below */
  const sceneOpacity = useTransform(
    scrollY,
    [0, typeof window !== 'undefined' ? window.innerHeight * 0.7 : 600],
    [1, 0]
  );

  const [sceneVisible, setSceneVisible] = useState(true);
  useMotionValueEvent(sceneOpacity, 'change', (v) => {
    setSceneVisible(v > 0.01);
  });

  return (
    <div className="portfolio-app">
      {/* Full-page animated background — dark:network image, light:polygon image */}

      {/* SiteBackground portal — renders into body, bypasses overflow:clip */}
      <SiteBackground />

      {/* Scroll progress bar */}
      <Motion.div
        className="scroll-progress-bar"
        style={{ scaleX: scrollYProgress }}
      />

      {/* Global cursor — fine-pointer only, replaces section-scoped cursor */}
      <Cursor systemSnapshot={systemSnapshot} />

      {/* 3D scene fades out as user scrolls past hero — deferred until after first paint */}
      {sceneReady && sceneVisible && (
        <Motion.div
          style={{ opacity: sceneOpacity, position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 1 }}
        >
          <Suspense fallback={null}>
            <Scene
              activeScene={sceneActive}
              systemSnapshot={systemSnapshot}
              lightingMode={systemSnapshot.lightingMode}
              keystrokeEnergy={0}
              wireframeMode={false}
              isMobileViewport={isMobileViewport}
              isUltraWide={typeof window !== 'undefined' && window.innerWidth >= 1920}
              hibernationTarget={false}
              motionState={motionState}
              reducedMotion={reducedMotion}
              scrollVelocity={scrollVelocity}
            />
          </Suspense>
        </Motion.div>
      )}

      <AmbientBackdrop
        scrollYProgress={scrollYProgress}
        reducedMotion={reducedMotion}
        isMobileViewport={isMobileViewport}
      />
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>

      <header className="portfolio-header" aria-label="Site header">
        <NeuralNavbar
          navigation={content.navigation}
          navScale={navScale}
          navY={navY}
          activeScene={sceneActive}
        />
      </header>

      <AnimatePresence>
        {showLoader ? (
          <LoaderScreen content={content} onSkip={dismissLoader} reducedMotion={reducedMotion} />
        ) : null}
      </AnimatePresence>

      {!showLoader && !reducedMotion && <PageCurtain />}

      <main id="main-content" className="portfolio-main" tabIndex={-1}>
        <HeroSection
          content={content}
          systemSnapshot={systemSnapshot}
          reducedMotion={reducedMotion}
          scrollY={scrollY}
        />
        <FounderSection content={content} reducedMotion={reducedMotion} />
        <CapabilitiesSection content={content} reducedMotion={reducedMotion} />
        <ProcessSection content={content} reducedMotion={reducedMotion} />
        <ProjectsSection content={content} reducedMotion={reducedMotion} />
        <PhilosophySection content={content} reducedMotion={reducedMotion} />
        <TrustBandSection content={content} reducedMotion={reducedMotion} />
        <ContactSection contact={content.contact} reducedMotion={reducedMotion} />
      </main>

      <Footer brand={content.brand} contact={content.contact} />
    </div>
  );
}

export default App;
